import {FC, useState} from "react";
import {useTranslation} from "react-i18next";
import {TabRow} from "../../../tab-content";
import "./OperationalAddress.scss";
import {AddressTableBody} from "../../overview/AddressTableBody";
import {
  OrganizationAddress,
  Source
} from "@common-core/coat-operational-hierarchy-appsync-model";
import {useGraphqlLazyQuery} from "../../../amplify/hooks";
import {lookupClientEntitiesByIds as lookupClientEntitiesByIdsQuery} from "../../../amplify/graphql";
import {
  LookupClientEntitiesByIdsData,
  LookupClientEntitiesByIdsVariables,
  assertClientEntitiesPresent
} from "../../backend";
import {useToastErrorHandler} from "../../../backend";
import {PermissionBasedEditButton} from "../../../action-buttons";
import {OnCompleteCallback} from "../../../utils";
import {EditAddressModal} from "./EditAddressModal";
import {SimpleTable} from "@interstate/components/SimpleTable";
import {EDIT_ADDRESS} from "../../../access";
import {collectPrimaryAddresses, PrimaryClientAddress} from "./addresses";

export interface AddressProps extends OrganizationAddress {
  id: string;
  sources?: Source[];
}

export const OperationalAddress: FC<AddressProps> = ({
  id,
  streetAddressLine1,
  streetAddressLine2,
  city,
  state,
  zip,
  country,
  sources
}) => {
  const {t} = useTranslation();
  const [primaryAddresses, setPrimaryAddresses] = useState<
    PrimaryClientAddress[]
  >([]);
  const [editingAddress, setEditingAddress] = useState<boolean>(false);
  const toastError = useToastErrorHandler();

  const [lookUpClientEntities, {loading}] = useGraphqlLazyQuery<
    LookupClientEntitiesByIdsData,
    LookupClientEntitiesByIdsVariables
  >({
    amplifyQuery: lookupClientEntitiesByIdsQuery
  });

  const beginEditing = () => {
    if (sources && sources.length > 0) {
      const ids = sources
        .map(source => source.sourceId)
        .filter(id => id != null); // Remove null and undefined values
      lookUpClientEntities({variables: {ids}})
        .then(assertClientEntitiesPresent)
        .then(collectPrimaryAddresses)
        .then(setPrimaryAddresses)
        .then(() => {
          setEditingAddress(true);
        })
        .catch(toastError);
    } else {
      setEditingAddress(true);
    }
  };

  const finishAddressEditing: OnCompleteCallback = () => {
    setEditingAddress(false);
  };

  return (
    <TabRow
      name={"operational-address"}
      header={
        <h4>
          {t("entity-detail.dealer.address.details")}
          <PermissionBasedEditButton
            onClick={beginEditing}
            loading={loading}
            qualifier={"address"}
            tooltip={t("entity-detail.dealer.address.edit")}
            inline={false}
            permissions={[EDIT_ADDRESS]}
          />
        </h4>
      }>
      {editingAddress && (
        <EditAddressModal
          id={id}
          currentAddress={
            {
              streetAddressLine1,
              streetAddressLine2,
              city,
              state,
              zip,
              country
            } as OrganizationAddress
          }
          primaryAddresses={primaryAddresses}
          onComplete={finishAddressEditing}
        />
      )}
      <SimpleTable
        id={`operational-address-table-${id}`}
        data-testid={`operational-address-table-${id}`}
        dataDensity={"small"}
        background={"white"}
        className={"operational-address-table"}
        bordered={false}
        hover={false}
        striped={false}>
        <AddressTableBody
          id={id}
          streetAddressLine1={streetAddressLine1}
          streetAddressLine2={streetAddressLine2}
          city={city}
          state={state}
          country={country}
          zip={zip}
        />
      </SimpleTable>
    </TabRow>
  );
};
